import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Typography,
  theme,
  Tooltip,
  Tag,
  notification,
} from 'antd';
import {
  FileTextOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import CommitteeAPI from '../../restAPI/CommitteeAPI';
import MasterDataAPI from '../../restAPI/MasterDataAPI';
import MeetingAPI from '../../restAPI/MeetingAPI';
import dayjs from 'dayjs';
import PaperCategoryAPI from '../../restAPI/PaperCategoryAPI';
import VotingSummaryDrawer from './VotingSummaryDrawer';
import CommonSkeletonLoader from '../../helperComponents/CommonSkeleton';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function MeetingDocumentsApprovalReport() {
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();
  const { companyId, token, userCompany } = useSelector((state) => state.auth);
  const [api, contextHolder] = notification.useNotification();
  
  // Get logged-in user's name
  const loggedInUserName = useMemo(() => {
    const companyInfo = userCompany?.companyData?.find(
      (company) => company.companyId === companyId
    );
    return companyInfo?.name || 'System User';
  }, [userCompany, companyId]);

  // Filter state
  const [form] = Form.useForm();
  const [generated, setGenerated] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Dropdown options
  const [committeeTypes, setCommitteeTypes] = useState([{ label: 'All', value: 'all' }]);
  const [committees, setCommittees] = useState([{ label: 'All', value: 'all' }]);
  const [paperCategories, setPaperCategories] = useState([{ label: 'All', value: 'all' }]);
  const [selectedCommitteeId, setSelectedCommitteeId] = useState(null);
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
      placement: 'bottomLeft',
    });
  };

  // Fetch committee types and committees on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true);
      try {
        // Guard: check if userCompany and companyData exist
        if (!userCompany || !userCompany.companyData) {
          setInitialLoading(false);
          return;
        }

        // Fetch committee types
        const companyInfo = userCompany.companyData.find(
          (company) => company.companyId === companyId
        );
        console.log("Fetching initial data for companyInfo: ", companyInfo);

        // Guard: check if companyInfo was found
        if (!companyInfo || !companyInfo.userDetailsId) {
          setInitialLoading(false);
          return;
        }

        const committeeTypesRes = await CommitteeAPI.getCommitteeTypeByUserDetailsId(
          companyInfo.userDetailsId,
          companyId,
          token
        );
        // console.log("fetch committee types response:", committeeTypesRes);
        if (committeeTypesRes?.data?.data) {
          const types = committeeTypesRes.data.data
            .filter((ct) => ct.status)
            .map((ct) => ({ label: ct.name, value: ct.id }));
          setCommitteeTypes([{ label: 'All', value: 'all' }, ...types]);
        }

        // Fetch committees
        const committeesRes = await MasterDataAPI.getCommitteeListDetailsByCompanyId(companyId, token);
        // console.log("fetch committees response:", committeesRes);
        if (committeesRes?.data) {
          const comms = committeesRes.data
            .filter((c) => 
              c.status && 
              (c.secretaryId === companyInfo.userDetailsId || c.assistantSecretaryId === companyInfo.userDetailsId)
            )
            .map((c) => ({ label: c.name, value: c.id }));
          setCommittees([{ label: 'All', value: 'all' }, ...comms]);
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
        openNotificationWithIcon('error', 'Error', 'Failed to fetch initial data.');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [companyId, token, userCompany]);

  // Reset filters and clear results when company changes
  useEffect(() => {
    const today = dayjs();
    form.resetFields();
    form.setFieldsValue({
      committeeType: 'all',
      committee: 'all',
      paperCategory: 'all',
      dateRange: [today, today],
    });
    setSelectedCommitteeId('all');
    setTableData([]);
    setPagination({ current: 1, pageSize: 10, total: 0 });
    setGenerated(false);
  }, [companyId, form]);

  // Fetch paper categories when committee is selected
  useEffect(() => {
    const fetchPaperCategories = async () => {
      if (!selectedCommitteeId || selectedCommitteeId === 'all') {
        setPaperCategories([{ label: 'All', value: 'all' }]);
        return;
      }

      try {
        // const res = await CommitteeAPI.getCommitteedataByTypeAndUserDetailsId(
        const res = await PaperCategoryAPI.getPaperCategoryList(
          selectedCommitteeId,
          companyId,
          token
        );
        // console.log("fetch paper categories response:", res);
        if (res?.data?.data) {
          const categories = res.data.data
            .filter((cat) => cat.status)
            .map((cat) => ({ label: cat.name, value: cat.id }));
          setPaperCategories([{ label: 'All', value: 'all' }, ...categories]);
        }
      } catch (err) {
        console.error('Error fetching paper categories:', err);
        openNotificationWithIcon('error', 'Error', 'Failed to fetch paper categories.');
        setPaperCategories([{ label: 'All', value: 'all' }]);
      }
    };

    fetchPaperCategories();
  }, [selectedCommitteeId, companyId, token]);

  // Fetch report data on mount with default date
  useEffect(() => {
    const today = dayjs();
    form.setFieldsValue({
      committeeType: 'all',
      committee: 'all',
      paperCategory: 'all',
      dateRange: [today, today],
    });
    setSelectedCommitteeId('all');
    fetchReportData({ 
      dateFrom: today.startOf('day').format(), 
      dateTo: today.endOf('day').format() 
    });
  }, []);

  const fetchReportData = async (params = {}, page = 1, size = 10) => {
    setLoading(true);
    try {
      const res = await MeetingAPI.getAttachmentApprovalReport(
        { ...params, page, size },
        companyId,
        token
      );
      
      if (res?.data?.data) {
        const { results, total } = res.data.data;
        const rows = results.map((item, index) => ({
          key: `${item.recordNumber || index}-${item.meetingId}-${item.agendaId}-${item.itemId}-${item.attachmentId}`,
          no: item.recordNumber,
          committeeType: item.committeeTypeName || '-',
          committeeName: item.committeeName || '-',
          meetingName: item.meetingName || '-',
          meetingStart: item.originalDate || '-',
          meetingEnd: item.originalDate || '-',
          paperCategory: item.paperCategoryName || '-',
          paperName: item.paperName || '-',
          paperCode: item.paperCode || '-',
          documentName: item.documentName || '-',
          comment: item.comment || '',
          votingEnd: item.votingEnd ? dayjs(item.votingEnd).format('YYYY-MM-DD') : '-',
          votingResult: item.votingResult || 'Pending',
          votingCounts: item.votingCounts || { approved: 0, rejected: 0, abstain: 0 },
          approver: item.approver || {},
          votes: item.votes || [],
          meetingId: item.meetingId,
          agendaId: item.agendaId,
          itemId: item.itemId,
          attachmentId: item.attachmentId,
        }));
        
        setTableData(rows);
        setPagination({ current: page, pageSize: size, total });
        setGenerated(true);
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      openNotificationWithIcon('error', 'Error', 'Failed to fetch report data.');
      setTableData([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const onGenerate = async () => {
    try {
      const values = await form.validateFields();
      const [from, to] = values.dateRange || [];
      
      const params = {};
      if (values.committeeType && values.committeeType !== 'all') {
        params.committeeTypeId = values.committeeType;
      }
      if (values.committee && values.committee !== 'all') {
        params.committeeId = values.committee;
      }
      if (values.meetingName) {
        params.meetingName = values.meetingName;
      }
      if (values.paperCategory && values.paperCategory !== 'all') {
        params.paperCategoryId = values.paperCategory;
      }
      if (values.paperName) {
        params.paperName = values.paperName;
      }
      if (values.documentName) {
        params.documentName = values.documentName;
      }
      if (from) {
        params.dateFrom = from.startOf('day').format();
      }
      if (to) {
        params.dateTo = to.endOf('day').format();
      }

      fetchReportData(params, 1, pagination.pageSize);
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  const onClear = () => {
    const today = dayjs();
    form.resetFields();
    form.setFieldsValue({
      committeeType: 'all',
      committee: 'all',
      paperCategory: 'all',
      dateRange: [today, today],
    });
    setSelectedCommitteeId('all');
    setPaperCategories([{ label: 'All', value: 'all' }]);
    fetchReportData({ 
      dateFrom: today.startOf('day').format(), 
      dateTo: today.endOf('day').format() 
    });
  };

  const handleTableChange = (newPagination) => {
    const values = form.getFieldsValue();
    const [from, to] = values.dateRange || [];
    
    const params = {};
    if (values.committeeType && values.committeeType !== 'all') {
      params.committeeTypeId = values.committeeType;
    }
    if (values.committee && values.committee !== 'all') {
      params.committeeId = values.committee;
    }
    if (values.meetingName) {
      params.meetingName = values.meetingName;
    }
    if (values.paperCategory && values.paperCategory !== 'all') {
      params.paperCategoryId = values.paperCategory;
    }
    if (values.paperName) {
      params.paperName = values.paperName;
    }
    if (values.documentName) {
      params.documentName = values.documentName;
    }
    if (from) {
      params.dateFrom = from.startOf('day').format();
    }
    if (to) {
      params.dateTo = to.endOf('day').format();
    }

    fetchReportData(params, newPagination.current, newPagination.pageSize);
  };

  // Fetch detailed voting summary
  const fetchVotingSummary = async (record) => {
    setDetailLoading(true);
    try {
      const res = await MeetingAPI.getAttachmentApprovalSummary(
        record.meetingId,
        record.meetingStart,
        record.attachmentId,
        companyId,
        token
      );
      
      if (res?.data?.data) {
        const summaryData = res.data.data;
        // Transform API response to match drawer's expected format
        const transformedRecord = {
          meetingName: summaryData.meetingName,
          meetingStart: summaryData.startDateTime,
          meetingEnd: summaryData.endDateTime,
          committeeType: summaryData.committeeType,
          committeeName: summaryData.committeeName,
          paperCategory: summaryData.paperCategory,
          paperName: summaryData.paperName,
          paperCode: summaryData.paperCode,
          documentName: summaryData.documentName,
          comment: summaryData.comment,
          votingEnd: summaryData.votingEndDateTime,
          votingResult: summaryData.votingResult,
          votingCounts: {
            approved: summaryData.approvedCount || 0,
            rejected: summaryData.rejectedCount || 0,
            abstain: summaryData.abstainCount || 0,
          },
          votes: summaryData.votingSummary?.map(vote => ({
            participant: vote.votedUserDetailsName || vote.participant || '-',
            vote: vote.vote,
            timestamp: vote.timestamp,
          })) || [],
        };
        
        setSelectedRecord(transformedRecord);
        setDetailVisible(true);
      }
    } catch (err) {
      console.error('Error fetching voting summary:', err);
      openNotificationWithIcon('error', 'Error', 'Failed to fetch voting summary details.');
    } finally {
      setDetailLoading(false);
    }
  };

  // Date range validation
  const disabledDate = (current, { from }) => {
    if (from && current) {
      return current.isBefore(from, 'day');
    }
    return false;
  };

  // Columns as specified
  const columns = [
    { title: 'No.', dataIndex: 'no', key: 'no', width: 60 },
    { title: 'Committee Type', dataIndex: 'committeeType', key: 'committeeType' },
    { title: 'Committee Name', dataIndex: 'committeeName', key: 'committeeName' },
    { title: 'Meeting Name', dataIndex: 'meetingName', key: 'meetingName' },
    { title: 'Paper Category', dataIndex: 'paperCategory', key: 'paperCategory' },
    { title: 'Paper Name', dataIndex: 'paperName', key: 'paperName' },
    { title: 'Document Name', dataIndex: 'documentName', key: 'documentName' },
    { title: 'Voting End', dataIndex: 'votingEnd', key: 'votingEnd' },
    { 
      title: 'Voting Result', 
      dataIndex: 'votingResult', 
      key: 'votingResult', 
      render: (v) => (
        <Tag color={v === 'Approved' ? 'success' : v === 'Rejected' ? 'error' : 'default'}>{v}</Tag>
      ) 
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => fetchVotingSummary(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];



  return (
    <div style={{ margin: 16 }}>
      {contextHolder}
      <Row style={{ marginBottom: 12 }}>
        <Col span={24}>
          <Breadcrumb
            items={[
              { href: '', title: <FileTextOutlined /> },
              { title: 'Reports' },
              { title: 'Meeting Documents Approval Report' },
            ]}
          />
        </Col>
      </Row>

      <Card style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
        {initialLoading ? (
          <CommonSkeletonLoader itemCount={8} showAvatar={false} />
        ) : (
          <>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>Meeting Documents Approval Report</Title>
                <Text type="secondary">Generate and review meeting document approval statuses.</Text>
              </Col>
              <Col>
                <Space>
                  {/* <Button icon={<FileExcelOutlined />} disabled={!generated}>Export to Excel</Button>
                  <Button icon={<FilePdfOutlined />} disabled={!generated}>Export to PDF</Button> */}
                </Space>
              </Col>
            </Row>

            {/* Filter Panel */}
            <Form form={form} layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item name="committeeType" label="Committee Type">
                    <Select options={committeeTypes} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="committee" label="Committee">
                    <Select 
                      options={committees} 
                      onChange={(value) => {
                        setSelectedCommitteeId(value);
                        form.setFieldValue('paperCategory', 'all');
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="meetingName" label="Meeting Name">
                    <Input placeholder="Search meeting name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="paperCategory" label="Paper Category">
                    <Select 
                      options={paperCategories} 
                      disabled={!selectedCommitteeId || selectedCommitteeId === 'all'}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item name="paperName" label="Paper Name">
                    <Input placeholder="Search paper name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="documentName" label="Document Name">
                    <Input placeholder="Search document name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="dateRange"
                    label="Date From - Date To"
                    rules={[
                      { required: true, message: 'Please select a valid date range.' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || value.length !== 2) {
                            return Promise.reject(new Error('Please select both start and end dates.'));
                          }
                          const [from, to] = value;
                          if (to.isBefore(from, 'day')) {
                            return Promise.reject(new Error('End date cannot be before start date.'));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <RangePicker 
                      format="YYYY-MM-DD"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4} style={{ display: 'flex', alignItems: 'end' }}>
                  <Form.Item>
                    <Space>
                      <Button type="primary" onClick={onGenerate} icon={<FileTextOutlined />} loading={loading}>Generate</Button>
                      <Button onClick={onClear} icon={<ReloadOutlined />}>Clear</Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Divider />

            {loading ? (
              <CommonSkeletonLoader itemCount={pagination.pageSize} showAvatar={false} />
            ) : (
              <Table
                columns={columns}
                dataSource={tableData}
                locale={{ emptyText: generated ? 'No records found' : 'Use filters and click Generate' }}
                rowKey={(r) => r.key}
                pagination={pagination}
                onChange={handleTableChange}
              />
            )}
          </>
        )}

        <VotingSummaryDrawer
          visible={detailVisible}
          onClose={() => {
            setDetailVisible(false);
            setSelectedRecord(null);
          }}
          selectedRecord={selectedRecord}
          loggedInUserName={loggedInUserName}
          loading={detailLoading}
        />
      </Card>
    </div>
  );
}
